import React from 'react';
import EpiContext from '@episerver/spa-core/Spa';
import Config from 'app/Config';
import BtnSubmit from 'app/Components/Shared/Buttons/BtnSubmit';
import ErrorPage from '../pages/ErrorPage/ErrorPage';
import {EsimUpgradePages, InnerPopupTypes, DispRsnTypes} from '../../enums/enums';
import FormActions from './GenericFormActions/FormActions';
import InfoDeliveryForm from './InfoDeliveryForm/InfoDeliveryForm';
import DarkPopUp from './DarkPopUp/DarkPopUp';
import AlertBox from './AlertBox/AlertBox';
import DescriptiveLink from './GenericFormActions/SubComponents/DescriptiveLink/DescriptiveLink';
import OverviewPanel from './OverviewPanel/OverviewPanel';
import MobileInputForm from './MobileInputForm/MobileInputForm';
import wifiIcon from '../../Assets/Svg/wifi.svg';
import esimSupportIcon from '../../Assets/Svg/esimSupport.svg';
import warningAlert from '../../Assets/Svg/warningAlert.svg';
import triangleWarning from '../../Assets/Svg/triangleWarning.svg';
import thumbUpIcon from '../../Assets/Svg/thumbUp.svg';
import lightBulbSvg from '../../Assets/Svg/lightBulb.svg';
import mobileSvg from '../../Assets/Svg/mobile.svg';
import {getMobileNumSeparatedByDash} from 'app/utilities/StringHelpers/StringHelpers';
import {
  setCurrentPage,
  setShowInnerPopup,
  setInnerPopupType,
  setSubscriberNumber,
  setPopupCloseBtnClicked,
  resetPopupInfo,
  resetState,
  getEsimPriceIfPreChecksPass,
  isSubscriberEligibleForEsimUpgrade,
  sendOrderForEsimUpgrade,
} from '../../BusinessClientEsimUpgradeSlice';
import {
  ESIM_INFO_TEXT,
  SUPPORTED_DEVICES_TEXT,
  CHANGE_TO_ESIM_WIFI,
  CHANGE_TO_ESIM_WARNING,
  CHANGE_TO_ESIM_SUPPORT,
  CHANGE_TO_ESIM_SUCCESS_TEXT,
} from '../../constants/constants';
import {isValidILMobile} from 'app/utilities/StringHelpers/StringHelpers';
import BusinessClientSimChangeMain from '../../../BusinessClientSimChangeBlock/BusinessClientSimChangeMain';

const getStore = () => EpiContext.getStore();

const popupActions = {
  navigate: (popupType) => {
    const store = getStore();
    store.dispatch(setShowInnerPopup(true));
    store.dispatch(setInnerPopupType(popupType));
  },
  close: () => {
    getStore().dispatch(resetPopupInfo());
  },
  resetData: () => {
    getStore().dispatch(resetState());
  },
};

const createPageActions = (esimContentData = {}, sliceData = {}, contactOrgId = '') => {
  const {currentPage, subscriberNumber, esimPriceApiResponse} = sliceData || {};
  const {isSuspendedTheftOrLoss} = esimPriceApiResponse || {};
  const {businessUpgCourse, businessUpgCourseTheftOrLoss, businessProductId, businessPackage, businessCombSeq} = esimContentData?.expandedValue?.[0] || {};
  const upgKey = businessUpgCourse?.value || '';
  const upgKeyTheftOrLoss = businessUpgCourseTheftOrLoss?.value || '';
  const store = getStore();

  return {
    submitSubscriberAndFetchEsimPrice: (mobile) => {
      store.dispatch(setSubscriberNumber(mobile));
      store.dispatch(
        getEsimPriceIfPreChecksPass({
          subscriberNumber: mobile,
          upgKey,
          upgKeyTheftOrLoss,
          contactOrgId,
        }),
      );
    },
    submitSubscriberAndCheckEligibility: (mobile) => {
      store.dispatch(isSubscriberEligibleForEsimUpgrade({subscriberNumber: mobile}));
    },
    submitEsimOrder: (otpPhone) => {
      store.dispatch(
        sendOrderForEsimUpgrade({
          subscriberNumber,
          otpPhone,
          dispRsn: isSuspendedTheftOrLoss ? DispRsnTypes.SuspendedTheftOrLoss : DispRsnTypes.SubscriberIsActive,
          upgCourse: isSuspendedTheftOrLoss ? upgKeyTheftOrLoss : upgKey,
          productId: businessProductId?.value,
          package: businessPackage?.value,
          combSeq: businessCombSeq?.value,
        }),
      );
    },
    navigateToPage: (pageKey) => {
      store.dispatch(setCurrentPage(pageKey));
    },
  };
};

export const getPageByStatus = ({sliceData, contentData, contactOrgId, simSerialNumber}) => {
  const {currentPage, subscriberNumber, esimPriceApiResponse} = sliceData || {};
  const {isSuspendedTheftOrLoss} = esimPriceApiResponse || {};
  const {esimContentData, changeToSimComponentLink, welcomePageTitle, welcomePageSubtitle, simTitle, simSubtitle} = contentData || {};

  const pageActions = createPageActions(esimContentData, sliceData, contactOrgId);

  const pages = {
    [EsimUpgradePages.WelcomePage]: (
      <MobileInputForm
        title={welcomePageTitle?.value || 'החלפת SIM/eSIM'}
        subtitle={welcomePageSubtitle?.value || 'הכנסו את מספר הטלפון הנייד שלכם להמשך'}
        placeholder='הקלידו מספר טלפון נייד'
        additionalTextInputProps={{maxLength: '10', pattern: '[0-9]*', required: true}}
        errorMessage='הכנסו מספר טלפון תקין'
        btnText='אישור והזמנה'
        onClickCallback={pageActions.submitSubscriberAndFetchEsimPrice}
      />
    ),
    [EsimUpgradePages.ChoseEsimOrSim]: (
      <FormActions
        title='איזה סוג SIM תרצו?'
        optionsArr={[
          {
            isCheckedRequired: false,
            serviceName: 'SIM רגיל',
            price: 'ללא עלות',
            callback: () => pageActions.navigateToPage(EsimUpgradePages.BusinessClientSimChangeComponent),
          },
          {
            isCheckedRequired: true,
            serviceName: 'eSIM',
            price: esimPriceApiResponse.esimPrice,
            priceNote: 'עלות חד פעמית',
            callback: () => {
              pageActions.submitSubscriberAndCheckEligibility(subscriberNumber);
            },
          },
        ]}
        optionNotSelectedError='אנא בחר אפשרות להמשך'
        checkBoxNotCheckedError='אנא אשר את התנאים להמשך'
        checkBoxText='בדקתי והמכשיר תומך ב-eSIM'
        descriptiveLinksArr={[
          {
            title: 'מה זה eSIM?',
            isUnderlined: true,
            callback: () => popupActions.navigate(InnerPopupTypes.EsimInformation),
          },
          {
            title: 'האם המכשיר שלי תומך ב-eSIM?',
            isUnderlined: true,
            callback: () => popupActions.navigate(InnerPopupTypes.SupportedDevices),
          },
        ]}
        btnText='המשך'
      />
    ),
    [EsimUpgradePages.ChangeToEsim]: (
      <InfoDeliveryForm
        title='החלפה ל-eSIM'
        purpleSubTitle={`למספר ${getMobileNumSeparatedByDash(subscriberNumber)}`}
        infoPanelTitle='בדקתי והמכשיר תומך ב-eSIM'
        infoPanelContentArr={[
          {
            icon: wifiIcon,
            text: CHANGE_TO_ESIM_WIFI,
          },
          {
            icon: triangleWarning,
            text: CHANGE_TO_ESIM_WARNING,
          },
          {
            icon: esimSupportIcon,
            text: CHANGE_TO_ESIM_SUPPORT,
          },
        ]}
        descriptiveLinksArr={[
          {
            title: 'מה זה eSIM?',
            isUnderlined: true,
            callback: () => popupActions.navigate(InnerPopupTypes.EsimInformation),
          },
          {
            title: 'מכשירים תומכים',
            isUnderlined: true,
            callback: () => popupActions.navigate(InnerPopupTypes.SupportedDevices),
          },
        ]}
        textInputWrapperObj={{
          wrapperTitle: 'לאיזה מספר תרצו לשלוח את הוראות ההפעלה?',
          validityCallback: isSuspendedTheftOrLoss
            ? (mobileNumber) => isValidILMobile(mobileNumber) && mobileNumber !== subscriberNumber
            : () => true,
          initialInputValue: isSuspendedTheftOrLoss ? '' : subscriberNumber,
          errorMessage: 'אנא הזינו מספר טלפון תקין השונה ממספר המנוי המושהה',
        }}
        textInputObj={
          isSuspendedTheftOrLoss
            ? {
                placeholder: 'מספר טלפון',
                maxLength: '10',
                pattern: '[0-9]*',
              }
            : {
                placeholder: 'מספר טלפון',
                disabled: true,
                value: subscriberNumber,
              }
        }
        btnText='אישור והזמנה'
        onClickCallback={pageActions.submitEsimOrder}
      />
    ),
    [EsimUpgradePages.ChangeToEsimSuccess]: (
      <OverviewPanel
        icon={thumbUpIcon}
        title='ההזמנה בוצעה בהצלחה'
        subtitle='בואו נפעיל את ה-eSIM בכמה צעדים פשוטים'
        infoPanelObj={{
          titleIcon: mobileSvg,
          title: getMobileNumSeparatedByDash(subscriberNumber),
          titleExtraInfo: 'הפעלת eSIM',
          contentArr: [
            {
              icon: lightBulbSvg,
              textTitle: 'כיצד מפעילים?',
              text: CHANGE_TO_ESIM_SUCCESS_TEXT,
              isTopIcon: true,
            },
          ],
        }}
        btnText='חזרה לאיזור האישי'
        btnOnClick={() => EpiContext.navigateTo(Config.businessLobbyUrl)}
      />
    ),

    [EsimUpgradePages.BusinessClientSimChangeComponent]: (
      <BusinessClientSimChangeMain
        subscriberNumber={subscriberNumber}
        pageTitle={simTitle?.value || 'החלפת סים'}
        pageSubtitle={simSubtitle?.value || 'אנא הזן מספר סים'}
        simSerialNumber={simSerialNumber}
      />
    ),

    //ERROR PAGES
    [EsimUpgradePages.Suspended]: (
      <ErrorPage
        icon={warningAlert}
        title='המנוי מושהה'
        subtitle='לא ניתן להעביר מנוי בסטטוס מושהה, להמשך טיפול אפשר לפנות לאחראי השירות'
        btn={<BtnSubmit text={'סיום'} onclick={() => EpiContext.navigateTo(Config.businessLobbyUrl)} />}
      />
    ),

    [EsimUpgradePages.NotBelongToOrg]: (
      <ErrorPage
        icon={warningAlert}
        title='המנוי לא שייך לארגון'
        subtitle='המספר שהוזן לא משויך לארגון שלכם. אנא ודאו שהמספר נכון או פנו לשירות הלקוחות'
        btn={<BtnSubmit text={'סיום'} onclick={() => EpiContext.navigateTo(Config.businessLobbyUrl)} />}
      />
    ),

    [EsimUpgradePages.BelongToDifferentOrg]: (
      <ErrorPage
        icon={warningAlert}
        title='המנוי שייך לארגון אחר'
        subtitle={`המספר שהוזן משויך לארגון אחר (${esimPriceApiResponse?.differentOrgName || 'שם ארגון לא ידוע'}, מזהה: ${
          esimPriceApiResponse?.differentOrgId || 'לא ידוע'
        }). אנא בדקו את המספר או פנו לשירות הלקוחות לקבלת סיוע`}
        btn={<BtnSubmit text={'סיום'} onclick={() => EpiContext.navigateTo(Config.businessLobbyUrl)} />}
      />
    ),

    [EsimUpgradePages.SubscriberIsInNiudProcess]: (
      <ErrorPage
        icon={warningAlert}
        title='המנוי נמצא בתהליך ניוד'
        subtitle='לא ניתן להשלים את ההחלפה כאשר המנוי נמצא בתהליך ניוד'
        btn={<BtnSubmit text={'סיום'} onclick={() => EpiContext.navigateTo(Config.businessLobbyUrl)} />}
      />
    ),

    [EsimUpgradePages.SubscriberIsAbroad]: (
      <ErrorPage
        icon={warningAlert}
        title='המנוי נמצא בחו"ל'
        subtitle='לא ניתן לבצע את ההחלפה כאשר המנוי נמצא בחו"ל'
        btn={<BtnSubmit text={'סיום'} onclick={() => EpiContext.navigateTo(Config.businessLobbyUrl)} />}
      />
    ),

    [EsimUpgradePages.GeneralError]: (
      <ErrorPage
        icon={warningAlert}
        title='מצטערים, משהו השתבש בתהליך ולא ניתן לבצע את הפעולה שביקשת'
        subtitle='אירעה שגיאה בלתי צפויה. אנא נסו שוב מאוחר יותר או פנו לשירות הלקוחות לקבלת סיוע'
        btn={<BtnSubmit text={'סיום'} onclick={() => EpiContext.navigateTo(Config.businessLobbyUrl)} />}
      />
    ),
  };

  return pages[currentPage] ?? pages[EsimUpgradePages.GeneralError];
};

const getInnerPopupInfoByType = (sliceData, contentData) => {
  const {innerPopupType, esimPriceApiResponse} = sliceData || {};
  const {esimContentData} = contentData || {};
  const {whatIsEsimPopupText, isDeviceSupportEsimPopupText} = esimContentData?.expandedValue?.[0] || {};

  const esimInfoText = whatIsEsimPopupText?.value || ESIM_INFO_TEXT;
  const supportedDevicesText = isDeviceSupportEsimPopupText?.value || SUPPORTED_DEVICES_TEXT;

  const innerPopupInfoByType = {
    [InnerPopupTypes.EsimInformation]: {
      headerObj: {
        title: 'מה זה eSIM?',
        text: <div dangerouslySetInnerHTML={{__html: esimInfoText}} />,
      },
      underlineTextObj: {
        title: 'האם המכשיר שלי תומך ב-eSIM?',
        isUnderlined: true,
        callback: () => popupActions.navigate(InnerPopupTypes.SupportedDevices),
      },
      btnText: 'הבנתי, תודה',
      btnCallback: popupActions.close,
      popupCloseBtnCallback: popupActions.close,
    },

    [InnerPopupTypes.SupportedDevices]: {
      headerObj: {
        title: 'בואו נבדוק אם המכשיר שלכם תומך',
        text: <div dangerouslySetInnerHTML={{__html: supportedDevicesText}} />,
      },
      btnText: 'הבנתי, תודה',
      btnCallback: popupActions.close,
      popupCloseBtnCallback: popupActions.close,
    },

    [InnerPopupTypes.CancelSuspensionConditions]: {
      headerObj: {
        title: 'תנאים להסרת השהייה',
        text: 'תנאים להסרת השהייה...',
      },
      btnText: 'הבנתי',
      btnCallback: () => popupActions.navigate(InnerPopupTypes.SuspendedTheftOrLoss),
      popupCloseBtnCallback: popupActions.close,
    },

    [InnerPopupTypes.SuspendedTheftOrLoss]: {
      headerObj: {
        icon: warningAlert,
        title: 'המנוי מושהה עקב אובדן/גניבה',
        text: 'בכדי להסיר את ההשהייה של המנוי, יש לקרוא תנאים ולסמן אישור. ההשהייה עבור אובדן/גניבה תוסר בסיום ההחלפה',
        checkboxObj: {
          text: (
            <>
              קראתי ואישרתי את&nbsp;
              <DescriptiveLink
                title='התנאים'
                isUnderlined={false}
                callback={() => popupActions.navigate(InnerPopupTypes.CancelSuspensionConditions)}
              />
              &nbsp;להסרת ההשהייה
            </>
          ),
        },
      },
      isCheckedRequired: true,
      aboveBtnError: 'כדי להמשיך יש לאשר את הסרת ההשהייה',
      btnText: 'אפשר להמשיך',
      btnCallback: popupActions.close,
      popupCloseBtnCallback: popupActions.resetData,
    },
    [InnerPopupTypes.GeneralError]: {
      headerObj: {
        icon: warningAlert,
        title: 'אופס, משהו השתבש',
        text: 'אירעה שגיאה בלתי צפויה. אנא נסו שוב מאוחר יותר או פנו לשירות הלקוחות לקבלת סיוע',
      },
      btnText: 'סיום',
      btnCallback: () => EpiContext.navigateTo(Config.businessLobbyUrl),
      popupCloseBtnCallback: popupActions.close,
    },
  };

  return innerPopupInfoByType[innerPopupType] ?? innerPopupInfoByType[InnerPopupTypes.GeneralError];
};

export const getInnerPopupByType = ({sliceData, contentData}) => {
  const innerPopupInfo = getInnerPopupInfoByType(sliceData, contentData);

  if (!innerPopupInfo) {
    return null;
  }

  return <AlertBox {...innerPopupInfo} />;
};
